import { useEffect, useState } from 'react';
import { MdOutlineClose } from 'react-icons/md';
import Input from '../forms/input';
import PhoneNumberInput from '../forms/phone';
import CountryAutoSuggest from '../forms/location';
import Cookies from 'js-cookie';
import apiRequest from '../../utils/request';

interface SettingsModalProps {
    isOpen: boolean;
    onClose: () => void;
}

interface UserProfile {
    id: number;
    email: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    profilePic?: string;
}

const SettingsModal: React.FC<SettingsModalProps> = ({ isOpen, onClose }) => {
    const [isClosing, setIsClosing] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [contact, setContact] = useState('');
    const [location, setLocation] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isFetching, setIsFetching] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const handleClose = () => {
        setIsClosing(true);
        setTimeout(() => {
            onClose();
            setIsClosing(false);
            setIsAnimating(false);
            setError('');
            setSuccess('');
        }, 300);
    };

    // Fetch user profile when modal opens
    useEffect(() => {
        const fetchUserProfile = async () => {
            if (!isOpen) return;

            const token = Cookies.get('p-ai-accessToken');
            if (!token) {
                setError('Please log in to view settings');
                return;
            }

            setIsFetching(true);
            try {
                const data = await apiRequest.request<{
                    statusCode: number;
                    response?: { data?: UserProfile };
                }>({
                    method: 'get',
                    url: '/users/profile',
                });

                if (data?.statusCode === 200 && data?.response?.data) {
                    const profile = data.response.data;
                    setFirstName(profile.firstName);
                    setLastName(profile.lastName);
                    setEmail(profile.email);
                    setContact(profile.phoneNumber);
                    // Location might not be in the API yet, keep it empty for now
                    setLocation('');
                } else {
                    setError('Failed to load user profile');
                }
            } catch (error) {
                console.error('Failed to fetch user profile:', error);
                setError('Failed to load user profile');
            } finally {
                setIsFetching(false);
            }
        };

        fetchUserProfile();
    }, [isOpen]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');
        setSuccess('');

        try {
            const data = await apiRequest.request<{
                statusCode: number;
                message?: string;
            }>({
                method: 'post',
                url: '/users/edit-profile',
                data: {
                    firstName,
                    lastName,
                    phoneNumber: contact,
                    address: location,
                },
            });

            if (data?.statusCode === 200) {
                setSuccess('Profile updated successfully!');
            } else {
                setError(data?.message || 'Failed to update profile');
            }
        } catch (error: any) {
            console.error('Failed to update profile:', error);
            setError(error?.response?.data?.message || 'Failed to update profile. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const isSubmitDisabled =
        !firstName.trim() || !lastName.trim() || !email.trim() || !contact.trim() || isLoading || isFetching;

    // Trigger slide-in animation when modal opens
    useEffect(() => {
        if (isOpen) {
            setTimeout(() => setIsAnimating(true), 10);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <>
            <div
                className={`absolute inset-y-0 left-0 z-500 w-full max-w-md overflow-hidden bg-background shadow-2xl transition-transform duration-400 ease-out ${
                    isClosing ? '-translate-x-full' : isAnimating ? 'translate-x-0' : '-translate-x-full'
                }`}
            >
                <div className="relative flex h-full flex-col ">
                    {/* Background */}
                    <div className="pointer-events-none absolute inset-0 z-0">
                        <img
                            src="/assets/background-skeleton.svg"
                            alt="Perspective grid background"
                            className="absolute bottom-0 left-1/2 w-[200%] -translate-x-1/2 select-none opacity-30"
                        />
                    </div>

                    {/* Header */}
                    <div className="h-[60px] relative z-10 flex items-center justify-between border-b border-[#e1e1e1] px-6 py-5">
                        <img src="/assets/logo-smaill.svg" alt="Podus AI" className="h-auto w-[30px]" />
                        <button
                            onClick={handleClose}
                            className="flex h-10 w-10 items-center justify-center rounded-full bg-neutral-100 transition-colors hover:bg-neutral-200"
                            aria-label="Close modal"
                        >
                            <MdOutlineClose size={24} />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="relative z-10 flex-1 overflow-y-auto px-6 py-8">
                        <h2 className="mb-8 text-2xl font-bold text-[#151515]">Settings</h2>

                        {isFetching ? (
                            <div className="flex items-center justify-center py-12">
                                <div className="text-sm text-neutral-600">Loading...</div>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="space-y-6">
                                {error && (
                                    <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-600">
                                        {error}
                                    </div>
                                )}

                                {success && (
                                    <div className="rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-600">
                                        {success}
                                    </div>
                                )}

                                {/* First Name */}
                                <Input
                                    id="firstName"
                                    name="firstName"
                                    title="First Name"
                                    placeholder="First Name"
                                    type="text"
                                    required
                                    value={firstName}
                                    onChange={(e) => setFirstName(e.target.value)}
                                />

                                {/* Last Name */}
                                <Input
                                    id="lastName"
                                    name="lastName"
                                    title="Last Name"
                                    placeholder="Last Name"
                                    type="text"
                                    required
                                    value={lastName}
                                    onChange={(e) => setLastName(e.target.value)}
                                />

                                {/* Email */}
                                <Input
                                    id="email"
                                    name="email"
                                    title="Email"
                                    placeholder="Email"
                                    type="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                />

                                {/* Contact */}
                                <PhoneNumberInput
                                    id="contact"
                                    name="contact"
                                    title="Contact"
                                    value={contact}
                                    onChange={setContact}
                                    required
                                />

                                {/* Location */}
                                <CountryAutoSuggest
                                    query={location}
                                    setQuery={setLocation}
                                    title="Location"
                                    placeholder="Enter your location"
                                />

                                {/* Update Button */}
                                <button
                                    type="submit"
                                    disabled={isSubmitDisabled}
                                    className={`w-full rounded-lg py-3.5 text-sm font-semibold text-white transition-colors ${
                                        isSubmitDisabled
                                            ? 'cursor-not-allowed bg-neutral-400'
                                            : 'bg-[#151515] hover:bg-[#2a2a2a]'
                                    }`}
                                >
                                    {isLoading ? 'Updating...' : 'Update'}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default SettingsModal;
